import { borderFeature, createBackground } from '../features';
import { compactBlockProps } from './compact';
import type { BlockEntity, DefaultBlock, SpecificBlock, ZeroBlock } from './schemas';

export type BlockVariant = 'default' | 'specific' | 'zero';

/**
 * Host-only values the package deliberately doesn't own: both colour literals
 * live in the colour-picker presets, so the host may pass its own rather than
 * have the package hard-code a duplicate.
 */
export type CreateBlockOptions = {
    /** `TRANSPARENT_VALUE` — seed background of a block. */
    transparentColor?: string;
};

/**
 * Seeded on all three breakpoints and then compacted, so what a new block
 * stores is exactly what a compacted existing one would.
 */
const everywhere = <T>(make: () => T) => ({ desktop: make(), tablet: make(), mobile: make() });

/** `id` is left blank: the host fills it in when it places the block. */
const baseDefaults = {
    id: '',
    children: [] as unknown[],
    entityType: 'block' as const,
    version: 0 as const,
    meta: {
        displayId: null,
        displayCondition: null,
    },
};

const createCommonProps = (options?: CreateBlockOptions) => ({
    /** The content column every block gets unless it says otherwise. */
    width: null as { value: number; unit: 'px' | 'vw' } | null,
    /** Content-driven; a canvas overrides it with one of its own. */
    height: everywhere(() => null as { value: number; unit: 'px' | 'vh' } | null),
    pin: null as 'top' | 'bottom' | null,
    syncWithDesktop: true,

    hidden: { desktop: false, tablet: false, mobile: false },

    padding: everywhere(() => ({ value: { top: 0, right: 0, bottom: 0, left: 0 }, unit: 'px' as const })),

    // The contour is a feature, so its default lives with its schema.
    border: borderFeature.createDefault(),

    background: createBackground({ transparentColor: options?.transparentColor, withBlockLayers: true }),

    shapeDividers: {
        top: everywhere(() => ({ isEnabled: false })),
        bottom: everywhere(() => ({ isEnabled: false })),
    },
});

export const createDefaultBlock = (options?: CreateBlockOptions): DefaultBlock => ({
    ...baseDefaults,
    meta: { ...baseDefaults.meta, createdAt: Date.now() },
    entitySubType: 'default',
    props: compactBlockProps(createCommonProps(options)),
});

export const createSpecificBlock = (options?: CreateBlockOptions): SpecificBlock => ({
    ...baseDefaults,
    meta: { ...baseDefaults.meta, createdAt: Date.now() },
    entitySubType: 'specific',
    props: compactBlockProps({
        ...createCommonProps(options),
        icon: { horizontalPosition: '', size: '', color: '' },
        textColor: '',
        horizontalPosition: '',
        hideContentOnMobile: false,
        hasSeparator: false,
        hideWidgetsOnMobile: {},
    }),
});

export const createZeroBlock = (options?: CreateBlockOptions): ZeroBlock => ({
    ...baseDefaults,
    meta: { ...baseDefaults.meta, createdAt: Date.now() },
    entitySubType: 'zero',
    props: compactBlockProps({
        ...createCommonProps(options),
        // A canvas is exactly as tall as it says, and starts empty.
        height: everywhere(() => ({ value: 0, unit: 'px' as const })),
        hideOverflow: false,
    }),
});

export function createBlock(variant: 'default', options?: CreateBlockOptions): DefaultBlock;
export function createBlock(variant: 'specific', options?: CreateBlockOptions): SpecificBlock;
export function createBlock(variant: 'zero', options?: CreateBlockOptions): ZeroBlock;
export function createBlock(variant?: BlockVariant, options?: CreateBlockOptions): BlockEntity;
export function createBlock(variant: BlockVariant = 'default', options?: CreateBlockOptions): BlockEntity {
    if (variant === 'specific') return createSpecificBlock(options);
    if (variant === 'zero') return createZeroBlock(options);

    return createDefaultBlock(options);
}
